import axios from 'axios';
import { apiRoute } from '../constants/api-constants';

/**
 * THE RESIDENT PORTAL'S OWN DATA.
 *
 * Every route here is bounded server-side to the unit(s) the caller currently
 * lives in, through the SECURITY DEFINER readers added in migration 074. Read
 * that migration's header before adding anything: RLS does NOT narrow a pure
 * resident — they hold `app.scope_all = 'on'` so their own building is legible
 * at all — so a resident-facing endpoint that forgets its own filter returns the
 * whole organisation. There is no client-side equivalent of that guard and there
 * must never appear to be one: nothing here takes a unit or building parameter.
 */

export interface TenantUnit {
  id: number;
  unit_number: string;
  floor: string | null;
  moved_in: string | null;
  is_primary: boolean;
}

export interface TenantBuilding {
  id: number;
  property_name: string;
  address: string;
  country: string;
}

/**
 * Yours only, counted on the building's desk day (07:00–07:00 in its zone).
 * The building-wide visitorsToday / dailyToday are gone from the payload: they
 * were the building's operational figures, never a resident's.
 */
export interface TenantCounts {
  parcelsWaiting?: number;
  parcelsCollectedToday?: number;
  myVisitorsToday?: number;
  myVisitorsInside?: number;
}

/**
 * ONE HOME — an open occupancy of the caller's with its unit and building, one
 * row of lb_my_homes() (migration 114). Flat on purpose: it is the reader's row
 * as the server built it, plus the desk day it was counted on.
 *
 * `building_timezone` is already passed through the server's safe-zone check,
 * so it is the zone every time on the portal is printed in. `moved_in` is a
 * calendar DATE ('YYYY-MM-DD'), not an instant.
 */
export interface TenantHome {
  occupancy_id: string;
  unit_id: number;
  unit_number: string | null;
  unit_name: string | null;
  building_id: number;
  building_name: string;
  building_address: string | null;
  building_timezone: string;
  building_country: string | null;
  unit_floor: string | null;
  is_primary: boolean;
  moved_in: string | null;
  relationship: string | null;
  desk_day: { from: string; to: string };
  counts: TenantCounts;
}

export interface TenantOverview {
  profile: { first_name: string; last_name: string } | null;
  building: TenantBuilding | null;
  unit: TenantUnit | null;
  /** The primary home (first by is_primary, then newest move-in). Present from
   *  RP-1.2; optional so a portal talking to an older server still renders. */
  home?: TenantHome | null;
  /** Every open home, the primary one first. */
  homes?: TenantHome[];
  /** Across every home, on the primary home's desk day — the headline. */
  counts: TenantCounts;
}

/**
 * Every status the packages table actually holds — `redacted` included.
 *
 * It was missing here, so a redacted row (a collected parcel whose details were
 * erased) matched none of the cases the portal renders and fell through to the
 * catch-all, which showed residents the word for a mis-logged entry against a
 * parcel they had in fact collected. The type was narrower than the data.
 */
export type ParcelStatus =
  | 'awaiting_pickup'
  | 'picked_up'
  | 'returned'
  | 'redacted'
  | 'void';

export interface TenantParcel {
  id: string;
  unit_label: string | null;
  carrier: string | null;
  /**
   * The last four of the tracking number, never the whole thing — it is AES-GCM
   * at rest precisely so it does not travel, and four digits are enough to
   * recognise a parcel you were expecting.
   */
  tracking_last4: string | null;
  parcel_count: number | null;
  shelf: string | null;
  status: ParcelStatus;
  logged_at: string;
  picked_up_at: string | null;
  /** The zone of the home this parcel is for (resident-portal RP-9.9): its
   *  times are printed in it. Null or absent = the primary home's. */
  building_timezone?: string | null;
}

export interface TenantVisitor {
  id: number;
  visitor_type: string;
  visitor_details: string;
  /** First name only — see the note in the backend route. */
  first_name: string;
  party_size: number;
  arrived_at: string;
  departed_at: string | null;
  still_inside: boolean;
  /** Signed in on one of the household's visitor passes (Plan 3). Optional so
   *  the portal still renders against a server that does not send it. */
  on_pass?: boolean;
  /** The zone of the home this visit was to (RP-9.9). Null or absent = the
   *  primary home's. */
  building_timezone?: string | null;
}

export const getTenantOverview = async (): Promise<TenantOverview> => {
  const { data } = await axios.get(`${apiRoute}/tenant/overview`);

  return data as TenantOverview;
};

/**
 * One page of a portal list. `next_cursor` is opaque — pass it back as
 * `before` for the next page; null means there is nothing older. `total` is the
 * whole list's size, for "Showing X of Y".
 *
 * Parcels: the FIRST page carries every parcel still waiting on the shelf plus
 * the newest collected ones; later pages carry only collected/returned ones.
 */
export interface TenantPage<T> {
  items: T[];
  next_cursor: string | null;
  total: number;
}

export const getTenantParcelsPage = async (
  opts: { limit?: number; before?: string | null } = {}
): Promise<TenantPage<TenantParcel>> => {
  const { data } = await axios.get(`${apiRoute}/tenant/parcels`, {
    params: { limit: opts.limit ?? 40, ...(opts.before ? { before: opts.before } : {}) },
  });

  return {
    items: (data?.parcels || []) as TenantParcel[],
    next_cursor: data?.next_cursor ?? null,
    total: Number(data?.total) || 0,
  };
};

export const getTenantVisitorsPage = async (
  opts: { limit?: number; before?: string | null } = {}
): Promise<TenantPage<TenantVisitor>> => {
  const { data } = await axios.get(`${apiRoute}/tenant/visitors`, {
    params: { limit: opts.limit ?? 40, ...(opts.before ? { before: opts.before } : {}) },
  });

  return {
    items: (data?.visitors || []) as TenantVisitor[],
    next_cursor: data?.next_cursor ?? null,
    total: Number(data?.total) || 0,
  };
};

export const getTenantParcels = async (
  limit = 40
): Promise<TenantParcel[]> => {
  const { data } = await axios.get(`${apiRoute}/tenant/parcels`, {
    params: { limit },
  });

  return (data?.parcels || []) as TenantParcel[];
};

export const getTenantVisitors = async (
  limit = 40
): Promise<TenantVisitor[]> => {
  const { data } = await axios.get(`${apiRoute}/tenant/visitors`, {
    params: { limit },
  });

  return (data?.visitors || []) as TenantVisitor[];
};
